import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Lock, Plus, Users } from 'lucide-react';

import { PaneHeader } from '@/components/settings/pane-header';
import { TemplateEditorPane } from '@/components/settings/message-templates/template-editor-pane';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  createMessageTemplate,
  deleteMessageTemplate,
  fetchMessageTemplates,
  fetchTemplateVariables,
  updateMessageTemplate,
} from '@/lib/api/message-templates';
import { groupTemplatesByScope } from '@/lib/messages/message-templates';
import { invalidateMessageTemplates } from '@/lib/stores/message-templates-store';

// Starter body for a brand-new template: the server rejects an empty body, and
// showing the shape up front is what teaches the {{token}} dialect.
const STARTER_BODY = 'Dear {{patientFullName}},\n\n';

function TemplateRow({ template, active, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(template)}
      aria-current={active ? 'true' : undefined}
      className={[
        'flex w-full flex-col items-start gap-0.5 rounded-lg border px-3 py-2.5 text-left transition',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-border bg-accent text-accent-foreground' : 'border-border hover:bg-accent/50',
      ].join(' ')}
    >
      <span className="flex w-full items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
          {template.title || 'Untitled template'}
        </span>
        {template.shortcut ? (
          <code className="shrink-0 rounded bg-background px-1 py-0.5 text-[10.5px] text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)]">
            /{template.shortcut}
          </code>
        ) : null}
      </span>
      <span className="w-full truncate text-[11px] text-muted-foreground">
        {template.category ? `${template.category} · ` : ''}
        {template.body.replace(/\s+/g, ' ').trim() || 'Empty'}
      </span>
    </button>
  );
}

function TemplateSection({ icon: Icon, title, hint, templates, activeId, onSelect }) {
  return (
    <section className="space-y-1.5">
      <div className="flex items-center gap-1.5 px-0.5">
        <Icon className="size-3.5 text-muted-foreground" aria-hidden />
        <span className="text-[10.5px] font-extrabold tracking-wide text-muted-foreground">
          {title.toUpperCase()}
        </span>
      </div>
      {templates.length === 0 ? (
        <p className="px-0.5 text-[11px] text-muted-foreground">{hint}</p>
      ) : (
        templates.map((template) => (
          <TemplateRow
            key={template.id}
            template={template}
            active={template.id === activeId}
            onSelect={onSelect}
          />
        ))
      )}
    </section>
  );
}

/**
 * Settings → Message templates. A list/detail pair over the message-template library
 * (MESSAGE_TEMPLATES.md), mirroring the Patient Queue editor's shape.
 *
 * Two scopes live in one list: the shared clinic library (writable with
 * settings:manage — the server decides, and says so per row via canEdit) and the
 * user's own private templates, which need no permission at all. Every mutation
 * invalidates the composer's cache so a just-edited template can never be inserted
 * in its old wording.
 */
export function MessageTemplatesSubPage() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [canEditShared, setCanEditShared] = useState(false);
  const [variables, setVariables] = useState([]);
  const [sampleVars, setSampleVars] = useState({});
  const [selectedId, setSelectedId] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    const [library, catalog] = await Promise.allSettled([
      fetchMessageTemplates(),
      fetchTemplateVariables(),
    ]);
    if (library.status === 'fulfilled') {
      setTemplates(library.value.items);
      setCanEditShared(library.value.canEditShared);
    } else {
      toast.error(
        library.reason instanceof Error ? library.reason.message : 'Could not load message templates',
      );
    }
    if (catalog.status === 'fulfilled') {
      setVariables(catalog.value.variables);
      setSampleVars(catalog.value.sample);
    } else {
      // Non-fatal: the editor still works, it just loses the chip palette and preview.
      toast.error('Could not load the variable list — the palette and preview are unavailable.');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const { shared, personal } = useMemo(() => groupTemplatesByScope(templates), [templates]);
  const selected = useMemo(
    () => templates.find((template) => template.id === selectedId) ?? null,
    [templates, selectedId],
  );

  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    try {
      // Someone with settings:manage opening this page is configuring the clinic, so
      // a new template defaults to the shared library; everyone else can only make
      // personal ones anyway.
      const created = await createMessageTemplate({
        draft: { shared: canEditShared, title: 'New template', body: STARTER_BODY, order: templates.length },
      });
      setTemplates((current) => [...current, created]);
      setSelectedId(created.id);
      void invalidateMessageTemplates();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create the template');
    } finally {
      setCreating(false);
    }
  };

  const handleSave = useCallback(
    async (draft) => {
      const updated = await updateMessageTemplate({ templateId: selectedId, draft });
      setTemplates((current) =>
        current.map((template) => (template.id === updated.id ? updated : template)),
      );
      void invalidateMessageTemplates();
      return updated;
    },
    [selectedId],
  );

  const handleDelete = async (template) => {
    if (!template?.id) return;
    const label = template.title || 'this template';
    if (!window.confirm(`Delete “${label}”? This cannot be undone.`)) return;
    try {
      await deleteMessageTemplate({ templateId: template.id });
      setTemplates((current) => current.filter((row) => row.id !== template.id));
      setSelectedId('');
      void invalidateMessageTemplates();
      toast.success('Template deleted.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete the template');
    }
  };

  if (selected) {
    return (
      <TemplateEditorPane
        key={selected.id}
        template={selected}
        variables={variables}
        sampleVars={sampleVars}
        canEditShared={canEditShared}
        saveTemplate={handleSave}
        onDelete={handleDelete}
        onBack={() => setSelectedId('')}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PaneHeader
        title="Message templates"
        description="Reusable replies for the patient chat, with variables like the patient's name and their next appointment. Insert one from the message box, or type / to search."
        action={
          <Button type="button" size="sm" onClick={handleCreate} disabled={creating}>
            <Plus className="mr-1.5 size-4" aria-hidden />
            New template
          </Button>
        }
      />
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <div className="mx-auto max-w-3xl space-y-6">
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading templates…</div>
          ) : (
            <>
              <TemplateSection
                icon={Users}
                title="Clinic templates"
                hint={
                  canEditShared
                    ? 'No shared templates yet. Create one to give the whole team the same wording.'
                    : 'No shared templates yet.'
                }
                templates={shared}
                activeId={selectedId}
                onSelect={(template) => setSelectedId(template.id)}
              />
              <TemplateSection
                icon={Lock}
                title="My templates"
                hint="No personal templates yet. These are private to you."
                templates={personal}
                activeId={selectedId}
                onSelect={(template) => setSelectedId(template.id)}
              />
              {!canEditShared ? (
                <p className="text-xs text-muted-foreground">
                  You don’t have permission to edit shared settings, so clinic templates are read-only. Your own
                  templates are always yours to change.
                </p>
              ) : null}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
